import React, { Fragment, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { cloneDeep } from "lodash";
import Modal from "../../Modal";
import Radio, { LanguageRadio } from "../../Radio";
import { LanguageCheckbox } from "../../Checkbox";
import Button from "../../Button";
import "./PracticeModal.scss";
import SIS from "Services/StudentInformationService";
import saxonIcon from "./icons/saxon.png";
import { setTrackingReport } from "../../../state/tracking/actions";
import icons from './icons'
import { getUserData } from "../../../../../services/API";
import { GetBrandedTutorName, GetBrandedAppName, GetBrandedStandardPracticeIcon, GetBrandedSkillScaffoldIcon } from "../../../../../services/BrandingService";
import { lang } from "moment";

export default function PracticeModalWrapper(props) {
  if (!props.practiceModalData) return null;
  return <PracticeModal {...props} />;
}

function PracticeModal(props) {
  const { practiceModalData } = props;

  // init modal input configuration if a single student is selected
  useEffect(() => {
    if (practiceModalData.length !== 1) return;
    const { metadata } = practiceModalData[0];
    setEsl(metadata.esl);
    setLanguageOverride(metadata.assessmentLanguageOverride);
    setTutorInSpanish(metadata.tutorInSpanish);
    setPracticeMode(metadata.early_reader ? "early_reader" : "standard");
    setPracticeModeBilingual(
      metadata.spanish_early_reader ? "early_reader" : "standard"
    );
  }, [practiceModalData]);

  const [esl, setEsl] = useState(false);
  const [assessmentLanguageOverride, setLanguageOverride] = useState(null); // ["ENGLISH_ONLY", "SPANISH_ONLY", "BOTH_ONE_SESSION"
  const [tutorInSpanish, setTutorInSpanish] = useState(false);
  const [practiceMode, setPracticeMode] = useState("standard");
  const [practiceModeBilingual, setPracticeModeBilingual] =
    useState("standard");
  const [page, setPage] = useState(practiceModalData.assessmentMode ? 5 : 1);
  const [waiting, setWaiting] = useState(false);

  const trackingReportData = useSelector(
    (state) => state.reports.tracking.report.data
  );

  const dispatch = useDispatch();
  const setReport = (data) => dispatch(setTrackingReport(data));

  const length = practiceModalData.length;
  const { first_name, last_name } = practiceModalData[0];

  let subject = `${first_name} ${last_name}`;
  if (length > 1) subject += ` and ${length - 1} other student`;
  if (length > 2) subject += "s";

  return (
    <Modal isOpen={true} renderCloseIcon={false} onClose={props.onClose}>
      <div className="practice-modal">{renderContent()}</div>
    </Modal>
  );

  function renderContent() {
    if (page === 1) {
      return (
        <Fragment>
          <div className="modal-title">Configure practice for {subject}</div>
          <div className="subtitle">{subject} will practice reading in:</div>
          <div style={{ display: "flex" }}>
            <LanguageRadio
              label="English"
              checked={!esl}
              onClick={() => setEsl(false)}
            />
            <LanguageRadio
              label="English and Spanish"
              checked={esl}
              onClick={() => setEsl(true)}
            />
          </div>
          <div className="controls">
            {renderCancel()}
            <Button onClick={() => setPage(2)}>Next</Button>
          </div>
        </Fragment>
      );
    }

    if (page === 2) {
      return (
        <Fragment>
          <PracticeConfiguration
            language="English"
            subject={subject}
            practiceMode={practiceMode}
            setPracticeMode={setPracticeMode}
            students={practiceModalData}
          />
          <div className="controls">
            {renderCancel()}
            <Button onClick={() => setPage(3)}>Next</Button>
          </div>
        </Fragment>
      );
    }

    if (page === 3) {
      return (
        <Fragment>
          <TutorConfiguration
            language="English"
            subject={subject}
            tutorInSpanish={tutorInSpanish}
            setTutorInSpanish={setTutorInSpanish}
          />
          <div className="controls">
            {renderCancel()}
            {esl ? (
              <Button onClick={() => setPage(4)}>Next</Button>
            ) : (
              <Button waiting={waiting} onClick={updateStudents}>
                Update
              </Button>
            )}
          </div>
        </Fragment>
      );
    }

    if (page === 4) {
      return (
        <Fragment>
          <PracticeConfiguration
            language="Spanish"
            subject={subject}
            practiceMode={practiceModeBilingual}
            setPracticeMode={setPracticeModeBilingual}
            students={practiceModalData}
          />
         <div className="controls">
            {renderCancel()}
            <Button waiting={waiting} onClick={updateStudents}>
              Update
            </Button>
          </div>
        </Fragment>
      );
    }

    if (page === 5) {
      return (
        <Fragment>
          <div className="modal-title">Configure bilingual assessments for {subject}</div>
          <div className="subtitle">Note: Only bilingual students will be updated</div>
          <div className="subtitle">District assignments for {subject} will be taken in:</div>
          <div style={{ display: "flex" }}>
            <LanguageRadio
              label="Distrct Default"
              checked={!assessmentLanguageOverride}
              onClick={() => setLanguageOverride(null)}
            />
            <LanguageRadio
              label="English"
              checked={assessmentLanguageOverride === "ENGLISH_ONLY"}
              onClick={() => setLanguageOverride("ENGLISH_ONLY")}
            />
            <LanguageRadio
              label="Spanish"
              checked={assessmentLanguageOverride === "SPANISH_ONLY"}
              onClick={() => setLanguageOverride("SPANISH_ONLY")}
            />
            <LanguageRadio
              label="English and Spanish"
              checked={assessmentLanguageOverride === "BOTH_ONE_SESSION"}
              onClick={() => setLanguageOverride("BOTH_ONE_SESSION")}
            />
          </div>
          <div className="controls">
            {renderCancel()}
            <Button waiting={waiting} onClick={updateStudents}>
              Update
            </Button>
          </div>
        </Fragment>
      );
    }
  }

  function renderCancel() {
    return (
      <Button
        type="secondary"
        style={{ marginRight: 12, width: "auto" }}
        onClick={props.onClose}
      >
        Cancel
      </Button>
    );
  }

  async function updateStudents() {
    setWaiting(true);

    // update esl and early_reader metadata for all selected students
    const early_reader = practiceMode === "early_reader";
    const spanish_early_reader = practiceModeBilingual === "early_reader";
    const saxon = practiceMode === "saxon";

    const update = practiceModalData.assessmentMode ? {assessmentLanguageOverride} :
    {
      esl,
      early_reader,
      spanish_early_reader,
      saxon,
      tutorInSpanish,
      assessmentLanguageOverride,
    };

    for (const student of practiceModalData) {
      const { studentId, metadata } = student;

      // Override the assessmentLanguageOverride for the student if the student is not an ESL student
      const updateToUse = !metadata.esl ? {...update, assessmentLanguageOverride: null} : update;
      await SIS.updateStudentMetadata(studentId, {
        ...metadata,
        ...updateToUse,
      });
    }

    // update metadata in the local state
    const ids = practiceModalData.map((s) => s.studentId);
    const trackingUpdate = cloneDeep(trackingReportData);
    for (const row of trackingUpdate) {
      const { studentId, metadata } = row;
      if (ids.includes(studentId)) row.metadata = { ...metadata, ...update };
    }

    setReport(trackingUpdate);
    props.onClose();
  }
}

function PracticeConfiguration(props) {
  const { subject, language, practiceMode, setPracticeMode } = props;
  const anySecondAndBelow = props.students.reduce((anyBelow,student)=>{return student.grade <= 2 ? true : anyBelow},false);
  const anyThirdAndAbove = props.students.reduce((anyAbove,student)=>{return student.grade >= 3 ? true : anyAbove},false);
  const saxonEnabled = getUserData().saxonEnabled && anySecondAndBelow;

  return (
    <Fragment>
      <div className="modal-title-wrapper">
        <div className="modal-title">
          Configure <span className="language-config">{language}</span> practice
          for {subject}
        </div>
        <LanguageIcon language={language} />
      </div>
      <PracticeRadio
        label={`Standard ${GetBrandedAppName()} Practice`}
        description={`Students read stories aloud with help from ${GetBrandedTutorName()} in the form of micro-interventions`}
        checked={practiceMode === "standard"}
        onClick={() => setPracticeMode("standard")}
        image={GetBrandedStandardPracticeIcon()}
      />
      <PracticeRadio
        label="Early Reader Skill Scaffold"
        description="A heavily scaffolded sequence of practice activities"
        checked={practiceMode === "early_reader"}
        onClick={() => setPracticeMode("early_reader")}
        image={GetBrandedSkillScaffoldIcon()}
      />
      {saxonEnabled && language === "English" && (
        <PracticeRadio
          label="Saxon"
          description={ "A leveled sequence of Saxon decodable stories" + (anyThirdAndAbove ? ". This will only be applied to any selected students in second grade or below" : "")}
          checked={practiceMode === "saxon"}
          onClick={() => setPracticeMode("saxon")}
          image={saxonIcon}
        />
      )}
    </Fragment>
  );
}

function TutorConfiguration(props) {
  const { subject, language, tutorInSpanish, setTutorInSpanish } = props;
  return (
    <Fragment>
      <div className="modal-title-wrapper">
        <div className="modal-title">
          Configure <span className="language-config">{language}</span> practice
          for {subject}
        </div>
        <LanguageIcon language={language} />
      </div>
      <div className="subtitle">
        {GetBrandedTutorName()} will tutor {subject} in the following language:
      </div>
      <div style={{ display: "flex" }}>
        <LanguageRadio
          label="English"
          checked={!tutorInSpanish}
          onClick={() => setTutorInSpanish(false)}
        />
        <LanguageRadio
          label="Spanish"
          checked={tutorInSpanish}
          onClick={() => setTutorInSpanish(true)}
        />
      </div>
    </Fragment>
  );
}

function PracticeRadio(props) {
  const { checked, image } = props;

  let className = "practice-radio";
  if (checked) className += " checked";

  return (
    <div className={className} onClick={props.onClick}>
      <div>
        <Radio label={props.label} checked={checked} />
        <div className="description">{props.description}</div>
      </div>
      <img src={image} className="practice-image" />
    </div>
  );
}

function LanguageIcon(props) {
  const { language } = props;

  if (language === "English") return <img src={icons.english} className="language-icon" />;
  if (language === "Spanish") return <img src={icons.spanish} className="language-icon" />;
  return null;
}
